import { Navigation } from '../home/components/Navigation';
import { Footer } from '../home/components/Footer';
import { useEffect } from 'react';
import { SEO } from '../../components/SEO';

export default function PrivacyPolicyPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  return (
    <>
      <SEO
        title="Privacy Policy | AI Receptionist Service"
        description="Read our privacy policy to understand how we collect, use, and protect your personal information when using our AI receptionist and phone answering services."
        canonical="/privacy-policy"
        schemas={[
          {
            type: 'WebPage',
            data: {
              '@type': 'WebPage',
              name: 'Privacy Policy',
              description: 'Privacy policy for AI Receptionist Service',
              url: `${siteUrl}/privacy-policy`,
              publisher: {
                '@type': 'Organization',
                name: 'AI Receptionist Service',
                url: siteUrl
              }
            }
          }
        ]}
      />
      <div className="min-h-screen bg-white">
        <Navigation />
        
        <div className="pt-32 pb-20 px-6">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-secondary mb-4">
              Dino Nuggets, LLC Privacy Policy
            </h1>
            <p className="text-gray-600 mb-12">
              Last Updated: January 2025
            </p>

            <div className="prose prose-lg max-w-none">
              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  1. Information We Collect
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  At Dino Nuggets, we collect information that you provide directly to us when you:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Fill out our contact or demo request forms</li>
                  <li>Subscribe to our services</li>
                  <li>Communicate with us via email, phone, or chat</li>
                  <li>Use our website and services</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  This information may include your name, email address, phone number, business name, and any other details you choose to provide.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  2. How We Use Your Information
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We use the information we collect to:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Provide, maintain, and improve our services</li>
                  <li>Respond to your inquiries and fulfill your requests</li>
                  <li>Send you technical notices, updates, and support messages</li>
                  <li>Communicate with you about products, services, and events</li>
                  <li>Monitor and analyze trends, usage, and activities</li>
                  <li>Detect, prevent, and address technical issues and fraudulent activity</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  3. SMS Opt-In and Mobile Information
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>SMS Opt-In:</strong> By providing your mobile phone number and opting in, you consent to receive SMS messages from Dino Nuggets, LLC, including service updates, appointment reminders, customer support messages, and promotional communications. Message frequency varies. Message and data rates may apply.
                </p>
                           
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Opt-Out:</strong> You may opt out of receiving SMS messages at any time by replying STOP to any message. For assistance, reply HELP.
                </p>

                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Mobile Information Sharing:</strong> We do not sell, rent, or share your mobile phone number or SMS opt-in consent data with third parties for their marketing purposes. Your mobile information may be shared only with:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Service providers who assist us in delivering SMS messages (such as SMS gateway providers), and only for the purpose of message delivery)</li>
                  <li>Internal systems used for scheduling, customer support, and service delivery</li>
                  <li>Legal authorities when required by law or to protect our rights</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  All service providers are contractually obligated to protect your information and use it solely for the services they provide to us.
                </p>

                <p className="text-gray-700 leading-relaxed mt-4">
                  <strong>Data Security:</strong> Your mobile phone number and related information are stored securely and protected using industry-standard safeguards.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  4. Information Sharing and Disclosure
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li><strong>Service Providers:</strong> We may share information with third-party vendors who perform services on our behalf</li>
                  <li><strong>Legal Requirements:</strong> We may disclose information if required by law or in response to valid legal requests</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  5. Data Security
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  We take reasonable measures to help protect your personal information from loss, theft, misuse, unauthorized access, disclosure, alteration, and destruction. However, no internet or email transmission is ever fully secure or error-free.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  6. Cookies and Tracking Technologies
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  We use cookies and similar tracking technologies to collect information about your browsing activities. You can control cookies through your browser settings, but disabling cookies may affect your ability to use certain features of our website.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  7. Your Rights and Choices
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  You have the right to:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Access and receive a copy of your personal information</li>
                  <li>Correct or update your personal information</li>
                  <li>Request deletion of your personal information</li>
                  <li>Opt-out of marketing communications</li>
                  <li>Object to processing of your personal information</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  8. Children's Privacy
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  Our services are not directed to children under 13 years of age. We do not knowingly collect personal information from children under 13. If you become aware that a child has provided us with personal information, please contact us.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  9. Changes to This Privacy Policy
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-secondary mb-4">
                  10. Contact Us
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  If you have any questions about this Privacy Policy, please contact us at:
                </p>
                <div className="mt-4 p-6 bg-gray-50 rounded-lg">
                  <p className="text-gray-700"><strong>Dino Nuggets, LLC</strong></p>
                  <p className="text-gray-700">Email: support@Dino-Nuggets.com</p>
                  <p className="text-gray-700">Phone: (586) 789-9699</p>
                </div>
              </section>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}
